<template>
  <div class="adsb-search-container">
    <el-form
      :model="searchForm"
      class="adsb-search-form"
      label-width="100px"
      size="small"
    >
      <!-- ========== 信息查询 ========== -->
      <div class="section-group">
        <div class="section-header">
          <span class="section-icon">📡</span>
          <span class="section-title">信息查询</span>
        </div>
        <div class="section-body">
          <el-row :gutter="0">
            <el-col :span="24">
              <el-form-item label="ICAO码">
                <div class="icao-select-wrapper">
                  <el-select
                    v-model="searchForm.icao"
                    placeholder="请选择或输入"
                    style="flex: 1"
                    filterable
                    clearable
                    :loading="loadingICAO"
                  >
                    <el-option
                      v-for="icao in icaoList"
                      :key="icao"
                      :label="icao"
                      :value="icao"
                    />
                  </el-select>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="监测范围">
                <el-switch
                  v-model="searchForm.showRange"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
                  @change="onRangeSwitchChange"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <div class="form-actions">
            <el-button
              type="primary"
              icon="el-icon-search"
              :loading="loadingTrace"
              :disabled="!canFetchData"
              @click="fetchTraceData"
            >
              查询轨迹
            </el-button>
            <el-button icon="el-icon-refresh-right" @click="resetSearchForm">
              重置
            </el-button>
          </div>
        </div>
      </div>

      <!-- ========== 气象查询 ========== -->
      <div class="section-group">
        <div class="section-header">
          <span class="section-icon">🌤️</span>
          <span class="section-title">气象查询</span>
        </div>
        <div class="section-body">
          <div class="weather-section">
            <div
              v-for="item in weatherLayers"
              :key="item.key"
              class="layer-item"
              :class="{ active: item.active }"
              @click="toggleLayer(item)"
            >
              <span class="layer-name">{{ item.name }}</span>
              <span class="layer-status">{{ item.active ? '开启' : '关闭' }}</span>
              <span v-if="item.key === 'pressure' && item.active && loading" class="layer-loading">加载中...</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 数据结果提示 -->
      <transition name="el-fade-in">
        <div v-if="traceData.length > 0 || fetchError" class="status-messages">
          <el-alert
            v-if="traceData.length > 0"
            :title="`成功获取 ${traceData.length} 个轨迹点`"
            type="success"
            show-icon
            :closable="false"
          />
          <el-alert
            v-if="fetchError"
            :title="fetchError"
            type="error"
            show-icon
            @close="fetchError = ''"
          />
        </div>
      </transition>
    </el-form>

    <!-- 气压值提示框 -->
    <div v-if="tooltip.show" class="pressure-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
      <div class="tooltip-arrow"></div>
      <div class="tooltip-content">
        <strong>{{ tooltip.label }}</strong>
      </div>
    </div>

    <!-- 台风节点详细信息弹窗 -->
    <div v-if="typhoonNodePopup.show" class="typhoon-node-popup"
      :style="{ left: typhoonNodePopup.x + 'px', top: typhoonNodePopup.y + 'px' }">
      <div class="typhoon-node-content" v-html="typhoonNodePopup.html"></div>
    </div>

    <!-- 潮汐详情弹窗 -->
    <div v-if="tideDetailPopup.show" class="tide-detail-modal" @click.self="closeTideDetail">
      <div class="tide-detail-panel">
        <div class="tide-detail-header">
          <span>潮汐信息</span>
          <span class="tide-detail-close" @click="closeTideDetail">&times;</span>
        </div>
        <div class="tide-detail-body">
          <div v-if="tideDetailPopup.loading" class="tide-detail-loading">加载中...</div>
          <div v-else-if="tideDetailPopup.data">
            <div class="tide-detail-info">
              <span>潮汐位置：{{ tideDetailPopup.data.zhname || tideDetailPopup.data.name || '-' }}</span>
              <span>日期：{{ tideDetailPopup.data.date || '-' }}</span>
            </div>
            <table class="tide-detail-table" v-if="tideDetailPopup.data.tideTable && tideDetailPopup.data.tideTable.length">
              <thead>
                <tr><th>潮时</th><th>潮高</th><th>类型</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in tideDetailPopup.data.tideTable" :key="idx">
                  <td>{{ formatTideTime(item.fxTime) }}</td>
                  <td>{{ item.height }}m</td>
                  <td>{{ item.type === 'H' ? '高潮' : item.type === 'L' ? '低潮' : item.type }}</td>
                </tr>
              </tbody>
            </table>
            <div class="tide-chart-title" v-if="tideDetailPopup.data.tideHourly && tideDetailPopup.data.tideHourly.length">逐时潮高曲线</div>
            <div ref="tideChart" class="tide-chart" v-if="tideDetailPopup.data.tideHourly && tideDetailPopup.data.tideHourly.length"></div>
          </div>
          <div v-else class="tide-detail-empty">暂无数据</div>
        </div>
      </div>
    </div>

    <!-- 天气预报底部面板 -->
    <div v-if="forecastPanel.show" class="forecast-bottom-panel">
      <div class="forecast-panel-header">
        <div class="forecast-panel-title">
          <span>天气预报(7天)</span>
          <el-cascader
            v-model="selectedForecastCity"
            :options="forecastCityOptions"
            :props="{ expandTrigger: 'hover' }"
            placeholder="点击选择"
            size="mini"
            clearable
            filterable
            class="forecast-city-cascader"
            @change="onForecastCityChange"
          />
          <span class="forecast-panel-position">
            位置: {{ forecastPanel.position.lng.toFixed(2) }}°E, {{ forecastPanel.position.lat.toFixed(2) }}°N
          </span>
        </div>
        <div class="forecast-panel-actions">
          <span class="forecast-pick-btn" :class="{ active: forecastClickMode }" @click="toggleForecastPickMode">
            {{ forecastClickMode ? '点选模式 (点击地图选点)' : '点选位置' }}
          </span>
          <span class="forecast-panel-close" @click="closeForecastPanel">关闭</span>
        </div>
      </div>
      <div class="forecast-panel-body">
        <div v-if="forecastPanel.loading" class="forecast-panel-loading">加载中...</div>
        <div v-else-if="forecastPanel.data && forecastPanel.data.length" class="forecast-table-wrap">
          <table class="forecast-table">
            <thead>
              <tr>
                <th class="forecast-th-fixed">日期</th>
                <th v-for="(day, di) in forecastDays" :key="'w-'+di" :colspan="day.colspan" class="forecast-date-cell">
                  {{ day.label }}
                </th>
              </tr>
              <tr>
                <th class="forecast-th-fixed">小时</th>
                <th v-for="(col, ci) in forecastTableColumns" :key="'h-'+ci" class="forecast-hour-cell">
                  {{ String(col.hour).padStart(2, '0') + ':00' }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="forecast-td-fixed">温度 °C</td>
                <td v-for="(col, ci) in forecastTableColumns" :key="'t-'+ci" :class="getTempClass(col.temperature)">
                  {{ col.temperature != null ? col.temperature.toFixed(1) : '-' }}
                </td>
              </tr>
              <tr>
                <td class="forecast-td-fixed">风 m/s</td>
                <td v-for="(col, ci) in forecastTableColumns" :key="'w-'+ci" :class="getWindClass(col.windSpeed)">
                  {{ col.windSpeed != null ? col.windSpeed.toFixed(1) : '-' }}
                </td>
              </tr>
              <tr>
                <td class="forecast-td-fixed">阵风 m/s</td>
                <td v-for="(col, ci) in forecastTableColumns" :key="'g-'+ci" :class="getWindClass(col.gustSpeed)">
                  {{ col.gustSpeed != null ? col.gustSpeed.toFixed(1) : '-' }}
                </td>
              </tr>
              <tr>
                <td class="forecast-td-fixed">风向</td>
                <td v-for="(col, ci) in forecastTableColumns" :key="'dir-'+ci" class="forecast-dir-cell">
                  <span v-if="col.windDirection != null" class="wind-arrow" :style="{ transform: 'rotate(' + (col.windDirection + 180) + 'deg)' }">↑</span>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr>
                <td class="forecast-td-fixed">浪高 m</td>
                <td v-for="(col, ci) in forecastTableColumns" :key="'wave-'+ci">
                  {{ col.waveHeight != null ? col.waveHeight.toFixed(1) : '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="forecast-panel-empty">暂无预报数据</div>
      </div>
    </div>
  </div>
</template>

<script>
// import { WindLayer, WindLayerOptions, WindData } from "cesium-wind-layer";
import weatherMixin from "@/mixins/weatherMixin";
import {
  getICAOList,
  getTraceData,
  getRealTimeState,
  getAircraftStateData,
  getAdsbLocation,
  getAdsbLocationGJ,
} from "@/api/ADSB";
import io from "socket.io-client";
export default {
  name: "ADSBSearchPanel",
  mixins: [weatherMixin],
  data() {
    return {
      // 检索表单数据
      searchForm: {
        date: "", // 初始为空，在 created 中赋值
        icao: "", // 设为示例 ICAO 码
        showRange: true, // 默认开启监测范围
      },
      // 状态管理
      icaoList: [], // 提供几个样本 ICAO
      traceData: [], // 获取到的轨迹数据（核心，供主页面调用）
      loadingICAO: false, // ICAO加载中
      loadingTrace: false, // 轨迹数据加载中
      fetchError: "", // 数据获取错误信息
      allList: [], // 实时数据列表
    };
  },
  created() {
    this.searchForm.date = this.getTodayStr();
    // this.loadICAOList(); // 组件加载时自动加载 ICAO 码数据
    this.loadAll(); // 组件加载时自动加载实时数据

    // 初始化时，如果默认开启，则绘制圆
    if (this.searchForm.showRange) {
      this.$nextTick(() => {
        this.$bus.$emit("drawZhuzhouCircle");
      });
    }

    this.timer = setInterval(async () => {
      let response = await getAdsbLocation();
      let result = response.result || [];
      console.log("ADS-B location data:", response);
      this.$bus.$emit("initADSPointAll", { states: result });
      this.icaoList = result.map((item) => item.icao);
      this.allList = result;
    }, 4000); // 每分钟刷新一次实时数据
  },
  beforeDestroy() {
    this.weatherBeforeDestroy();
    this.$bus.$emit("clearADSPointAll");
    this.$bus.$emit("removeZhuzhouCircle");
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  computed: {
    // 是否选中了ICAO码
    hasICAOSelected() {
      return !!this.searchForm.icao;
    },
    // 是否满足获取数据的条件
    canFetchData() {
      return !!this.searchForm.date && !!this.searchForm.icao;
    },
  },
  methods: {
    async loadAll() {
      const loading = this.$loading({
        lock: true,
        text: "数据同步中...",
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.7)",
      });
      let response = await getAdsbLocation();
      let result = response.result || [];
      console.log("ADS-B location data:", response);
      this.$bus.$emit("initADSPointAll", { states: result }, true);
      this.icaoList = result.map((item) => item.icao);
      this.allList = result;
      if (this.icaoList.length === 0) {
        this.$message.info("暂无可用的ICAO数据");
      } else {
        this.$message.success(`成功加载 ${this.icaoList.length} 个ICAO码`);
      }
      loading.close();
    },
    /**
     * 初始化 ADS-B 飞机点位（适配 getAircraftStateData 数据结构）
     */
    initADSPoint(adsbData) {
      const aircraftList = (adsbData && adsbData.aircraft) || [];
      if (!Array.isArray(aircraftList) || aircraftList.length === 0) {
        this.$message?.warning?.("暂无可用 ADS-B 航班数据");
        return;
      }

      // 适配 Main.vue 的 initADSPoint 接口：res.states 结构与 OpenSky 一致
      const states = aircraftList.map((aircraft) => {
        const lon = Number(aircraft.lon);
        const lat = Number(aircraft.lat);
        const altRaw = aircraft.alt_baro ?? aircraft.alt_geom ?? aircraft.altitude ?? 0;
        const altitude = altRaw === "ground" || altRaw == null ? 0 : Number(altRaw);
        const callsign = (aircraft.flight || "").trim();
        const country = aircraft.r || aircraft.t || "unknown";
        const isOnGround =
          altRaw === "ground" ||
          aircraft.on_ground === true ||
          aircraft.alt_geom === "ground";
        const velocity = Number(aircraft.gs) || 0;
        const trueTrack = Number(aircraft.track) || 0;

        return [
          aircraft.hex || "", // icao24
          callsign, // callsign
          country, // origin_country（用注册号/机型占位）
          null, // time_position（无）
          null, // last_contact（无）
          lon, // longitude
          lat, // latitude
          altitude, // altitude
          isOnGround, // on_ground
          velocity, // velocity
          trueTrack, // true_track
        ];
      });

      this.$bus.$emit("initADSPointAll", { states }, adsbData);
    },
    /**
     * 获取今天日期字符串 (YYYY/MM/DD)
     */
    getTodayStr() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}/${m}/${d}`;
    },

    /**
     * 日期改变时的处理
     */
    onDateChange() {
      // 日期变化时，自动加载 ICAO
      if (this.searchForm.date) {
        this.loadICAOList();
      }
    },

    /**
     * 监测范围开关切换
     */
    onRangeSwitchChange(val) {
      if (val) {
        this.$bus.$emit("drawZhuzhouCircle");
      } else {
        this.$bus.$emit("removeZhuzhouCircle");
      }
    },

    /**
     * 获取指定日期所在月份的一号日期字符串 (YYYY/MM/DD)
     */
    getFirstDayOfMonth(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      return `${y}/${m}/01`;
    },

    /**
     * 加载指定日期的ICAO列表
     * 注：由于 adsbexchange 样本站点是静态目录，前端难以直接获取列表
     * 实际业务中应由后端代理并解析目录
     */
    async loadICAOList() {
      this.loadingICAO = true;
      this.fetchError = "";
      try {
        // 固定查询日期为 2026/02/01，不与时间控件联动
        const queryDate = "2026/02/01";
        const res = await getICAOList({ date: queryDate });
        console.log("后端返回数据:", res);
        const data = res.data.traces.slice(0, 800) || [];

        if (data.error) throw new Error(data.error);

        this.icaoList = data;

        if (this.icaoList.length === 0) {
          this.$message.info("2026/02/01 暂无可用的ICAO数据");
        } else {
          this.$message.success(`成功加载 ${res.data.traces.length} 个ICAO码`);
        }
      } catch (err) {
        console.error("加载ICAO列表失败：", err);
        this.icaoList = [];
        this.$message.error(`加载ICAO失败：${err.message}`);
      } finally {
        this.loadingICAO = false;
      }
    },

    /**
     * 获取并解析 adsbexchange 轨迹数据
     */
    async fetchTraceData() {
      this.loadingTrace = true;
      this.fetchError = "";
      this.traceData = [];
      try {
        // 查询日期改为 searchForm.date 所在月份的第一天
        //const queryDate = this.getFirstDayOfMonth(this.searchForm.date);

        const res = await getAdsbLocationGJ({
          icao: this.searchForm.icao,
        });
        //  let result = response.result || [];
        let adsbData = res.result || []; // 适配不同的响应结构
        if (adsbData.error) throw new Error(adsbData.error);

        // 解析 adsbexchange 特有的 trace 格式
        // 结构：{ timestamp: 1627776000, trace: [[delta_t, lat, lon, alt, gs, ...], ...] }
        // const baseTimestamp = adsbData.timestamp || 0;
        // const rawPoints = adsbData.trace || [];

        // this.traceData = rawPoints
        //   .map((p) => ({
        //     t: baseTimestamp + p[0], // 绝对时间戳
        //     latitude: p[1],
        //     longitude: p[2],
        //     altitude: p[3] === "ground" ? 0 : p[3], // 处理地面高度
        //     gs: p[4],
        //     track: p[5],
        //   }))
        //   .filter((point) => point.latitude && point.longitude);

        adsbData = adsbData.reverse();
        if (adsbData.length === 0) {
          this.$message.info("该ICAO暂无有效轨迹数据");
          return;
        }

        let info = JSON.parse(JSON.stringify(adsbData[0]));
        let has = this.allList.filter((item) => item.icao === this.searchForm.icao);
        if (has.length > 0) {
          info = has[0];
        }
        this.$bus.$emit("traceDataFetched", adsbData, info);
        this.$message.success(`成功解析 ${adsbData.length} 个轨迹点`);
      } catch (err) {
        this.fetchError = `获取轨迹数据失败：${err.message}`;
        this.traceData = [];
      } finally {
        this.loadingTrace = false;
      }
    },

    /**
     * 重置检索表单
     */
    resetSearchForm() {
      this.searchForm = {
        date: this.getTodayStr(),
        icao: "",
        showRange: true,
      };
      this.icaoList = [];
      this.traceData = [];
      this.fetchError = "";
      this.$bus.$emit("traceDataFetchedclearData");
    },
  },

  mounted() {
    // 监听主页面的重置事件
    // 默认风粒子图关闭，不自动初始化
  },
};
</script>

<style lang="scss" scoped>
.adsb-search-container {
  padding: 8px;
  width: 100%;
  box-sizing: border-box;
}

.adsb-search-form {
  background: transparent;
}

// ========== 分组区域 ==========
.section-group {
  margin-bottom: 14px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(144, 164, 184, 0.18);
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.08), rgba(64, 158, 255, 0.02));
  border-bottom: 1px solid rgba(228, 231, 237, 0.5);
}

.section-icon {
  font-size: 15px;
  line-height: 1;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5a6a;
  letter-spacing: 0.5px;
}

.section-body {
  padding: 8px 12px 10px;
}

.icao-select-wrapper {
  display: flex;
  align-items: center;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px dashed rgba(200, 210, 220, 0.4);
}

.status-messages {
  margin-top: 15px;
}

.status-messages .el-alert {
  margin-bottom: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* 适配浅灰背景 (#f1f1f1) 的清新风格 */
:deep(.el-form-item__label) {
  font-weight: 600;
  color: #4a5a6a; /* 深灰蓝，在浅灰背景下更有质感 */
  padding-right: 12px;
}

:deep(.el-input__inner) {
  background-color: #ffffff; /* 纯白输入框，从 #f1f1f1 背景中脱颖而出 */
  border: 1px solid #dcdfe6;
  color: #303133;
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

:deep(.el-input__inner:focus) {
  border-color: #409eff;
  box-shadow: 0 0 5px rgba(64, 158, 255, 0.2);
}

:deep(.el-input__inner::placeholder) {
  color: #c0c4cc;
}

:deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #909399;
}

:deep(.el-button--primary) {
  background-color: #409eff;
  border-color: #409eff;
  font-weight: 500;
}

:deep(.el-button--primary:hover) {
  background-color: #66b1ff;
  border-color: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
}

:deep(.el-button--default) {
  background-color: #ffffff;
  border-color: #dcdfe6;
  color: #606266;
}

:deep(.el-button--default:hover) {
  color: #409eff;
  border-color: #c6e2ff;
  background-color: #ecf5ff;
}

:deep(.el-form-item) {
  margin-bottom: 18px;
}

/* 提示信息美化 */
.status-messages .el-alert {
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid transparent;
}

.status-messages .el-alert--success {
  background-color: #f0f9eb;
  border-color: #e1f3d8;
  color: #67c23a;
}

.status-messages .el-alert--error {
  background-color: #fef0f0;
  border-color: #fde2e2;
  color: #f56c6c;
}

/* 图标颜色 */
:deep(.el-input__prefix),
:deep(.el-input__suffix) {
  color: #909399;
}

// ========== 气象图层切换 ==========
.weather-section {
  border: 1px solid rgba(228, 231, 237, 0.5);
  border-radius: 6px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.25);
  .layer-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    transition: all 0.2s;
    color: #555;
    background: rgba(255, 255, 255, 0.4);
    &:last-child { border-bottom: none; }
    &:hover {
      background: rgba(64, 158, 255, 0.06);
      color: #333;
    }
    &.active {
      background: rgba(24, 144, 255, 0.12);
      color: #1890ff;
      .layer-status { color: #52c41a; }
      &:hover { background: rgba(24, 144, 255, 0.18); color: #1677d3; }
    }
    .layer-name { font-size: 13px; font-weight: 500; }
    .layer-status {
      font-size: 11px;
      opacity: 0.75;
      font-weight: 500;
    }
    .layer-loading { font-size: 11px; color: #fa8c16; margin-left: 6px; }
  }
}

.pressure-tooltip {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  transform: translateX(-50%);
  .tooltip-content {
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    strong { font-size: 14px; display: block; }
  }
}

/* 台风节点信息弹窗 */
.typhoon-node-popup {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  animation: tooltipFadeIn 0.15s ease;
}

.typhoon-node-content {
  background: rgba(15, 25, 45, 0.92);
  color: #e0f0ff;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.8;
  border: 1px solid rgba(100, 180, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 10px rgba(40, 120, 255, 0.2);
  backdrop-filter: blur(8px);
  max-width: 220px;
}

.typhoon-node-content table {
  width: 100%;
}

.typhoon-node-content td {
  white-space: nowrap;
}

@keyframes tooltipFadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.tide-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tide-detail-panel {
  width: 520px;
  max-height: 80vh;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.tide-detail-header {
  background: #0078ff;
  color: #fff;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tide-detail-close {
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  user-select: none;
}

.tide-detail-body {
  padding: 16px;
  overflow-y: auto;
}

.tide-detail-loading,
.tide-detail-empty {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

.tide-detail-info {
  margin-bottom: 12px;
  font-size: 13px;
  color: #333;
  line-height: 2;
}

.tide-detail-info span {
  margin-right: 16px;
}

.tide-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 16px;
}

.tide-detail-table th,
.tide-detail-table td {
  border: 1px solid #e0e0e0;
  padding: 8px 10px;
  text-align: center;
}

.tide-detail-table th {
  background: #f5f7fa;
  color: #333;
  font-weight: bold;
}

.tide-detail-table td {
  color: #555;
}

.tide-chart-title {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.tide-chart {
  width: 100%;
  height: 220px;
}

/* 天气预报底部面板 - 深蓝主题 */
.forecast-bottom-panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 16px;
  background: rgba(8, 45, 80, 0.92);
  z-index: 15000;
  border-radius: 8px;
  margin: 0 16px;
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow: hidden;
}

.forecast-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: rgba(0, 30, 60, 0.8);
  border-bottom: 1px solid rgba(131, 233, 255, 0.25);
  border-radius: 8px 8px 0 0;
}

.forecast-panel-title {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 15px;
  font-weight: bold;
  color: #83e9ff;
}

.forecast-panel-position {
  font-size: 13px;
  font-weight: normal;
  color: rgba(131, 233, 255, 0.8);
}

.forecast-city-cascader {
  width: 180px;
  margin-left: 8px;
}

.forecast-panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.forecast-pick-btn {
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  color: #83e9ff;
  border: 1px solid rgba(131, 233, 255, 0.4);
  white-space: nowrap;
}

.forecast-pick-btn.active {
  background: rgba(9, 124, 245, 0.8);
  color: #fff;
  border-color: #1890ff;
}

.forecast-panel-close {
  background: #ff4d4f;
  color: #fff;
  font-size: 13px;
  padding: 4px 14px;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.forecast-panel-body {
  padding: 8px 12px 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.forecast-panel-loading,
.forecast-panel-empty {
  text-align: center;
  padding: 30px 0;
  color: rgba(131, 233, 255, 0.6);
  font-size: 14px;
}

.forecast-table-wrap {
  min-width: 100%;
}

.forecast-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.forecast-table th,
.forecast-table td {
  border: 1px solid rgba(131, 233, 255, 0.2);
  padding: 6px 4px;
  text-align: center;
  white-space: nowrap;
}

.forecast-th-fixed,
.forecast-td-fixed {
  background: rgba(0, 30, 60, 0.9);
  font-weight: bold;
  color: #83e9ff;
  position: sticky;
  left: 0;
  z-index: 2;
  width: 100px;
  min-width: 100px;
}

.forecast-date-cell {
  background: rgba(0, 80, 120, 0.5);
  color: #83e9ff;
  font-weight: bold;
  font-size: 11px;
  min-width: 60px;
}

.forecast-hour-cell {
  background: rgba(0, 40, 70, 0.5);
  color: rgba(131, 233, 255, 0.85);
  font-size: 11px;
  min-width: 44px;
}

.forecast-table td {
  color: #cbefff;
  min-width: 44px;
}

.forecast-dir-cell {
  font-size: 14px;
  color: #83e9ff;
}

.wind-arrow {
  display: inline-block;
  font-weight: bold;
  color: #83e9ff;
}

/* 温度背景色 - 冷到热 */
.forecast-table td.temp-freeze { background: #102b6a; color: #fff; }
.forecast-table td.temp-cold   { background: #1677ff; color: #fff; }
.forecast-table td.temp-cool   { background: #69b1ff; color: #fff; }
.forecast-table td.temp-mild   { background: #bae0ff; color: #333; }
.forecast-table td.temp-warm   { background: #ffe58f; color: #333; }
.forecast-table td.temp-hot    { background: #ff9c6e; color: #fff; }
.forecast-table td.temp-severe { background: #ff4d4f; color: #fff; }

/* 风速背景色 - 轻到强 */
.forecast-table td.wind-weak   { background: #d9f7be; color: #333; }
.forecast-table td.wind-light  { background: #95de64; color: #333; }
.forecast-table td.wind-mid    { background: #ffc53d; color: #333; }
.forecast-table td.wind-strong { background: #ff7a45; color: #fff; }
.forecast-table td.wind-severe { background: #ff4d4f; color: #fff; }
</style>
